import { Alert, Form, Modal, Select, message } from 'antd';
import { FC, memo, useCallback, useEffect, useState } from 'react';
import { assignUserRole } from 'services/api/users';
import { fetchRoles } from 'services/api/roles';
import { RoleListRow } from 'types/api/roles';
import { useAppSelector } from 'store/hooks';
import { selectUserData } from 'store/user/selectors';

import useCeiling from 'hooks/useCeiling';
import { showErrorMessage } from 'utils/showErrorMessage';
import { roleAssignRefusal } from 'utils/ceiling';

interface IProps {
  onClose(): void;
  refetch(): void;
  id: number;
  /** Preselects the admin's current role. */
  currentRoleId?: number | null;
}

const AssignRoleModal: FC<IProps> = ({
  id,
  currentRoleId,
  onClose,
  refetch
}) => {
  const [form] = Form.useForm();
  const [roles, setRoles] = useState<RoleListRow[]>([]);
  const [loading, setLoading] = useState(false);
  const currentUser = useAppSelector(selectUserData());
  const { isSuperAdmin } = useCeiling();
  // Only a superadmin can reach this on their own row: an ordinary admin is
  // equal to themselves, and equal is not strictly below, so the action is
  // disabled for them on the list. The first-person warning below is
  // therefore a superadmin-only path.
  const isSelf = currentUser?.id === id && isSuperAdmin;

  // You may only hand over a role whose permissions are a subset of your own,
  // and never a system role. Showing one you cannot grant just trades a
  // selectable option for a 403. The role they already hold stays listed even
  // if it is above your ceiling, so the field is never blank on open.
  //
  // ASSIGN, not edit: the row carries both answers, and they differ on a
  // system role — uneditable by everyone, still grantable by a superadmin.
  const assignable = roles.filter(
    role => role.id === currentRoleId || !roleAssignRefusal(role)
  );

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const rows = await fetchRoles();
        if (!cancelled) setRoles(rows);
      } catch (err) {
        if (!cancelled) showErrorMessage(err);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  const onFinish = useCallback(
    async (values: { roleId: number }) => {
      try {
        setLoading(true);
        await assignUserRole(id, values.roleId);
        message.success('Successfully updated');

        // Our own session is already gone at this point, so refetching would
        // 401 and surface an error toast on the way out. Let the interceptor
        // take us to the login screen instead.
        if (!isSelf) refetch();

        onClose();
      } catch (err) {
        showErrorMessage(err);
      } finally {
        setLoading(false);
      }
    },
    [id, isSelf, onClose, refetch]
  );

  return (
    <Modal
      open
      title="Change role"
      onCancel={onClose}
      width={600}
      onOk={form.submit}
      confirmLoading={loading}
    >
      {/*
        Saving ends every session this admin has. The role and its permission
        map are baked into the token at sign-in, so the BE deletes all of their
        session keys to make the change take effect at once — JwtStrategy then
        401s their next request and the panel signs them out, on every device.
      */}
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message={
          isSelf
            ? 'This signs you out immediately'
            : 'This signs them out of every device immediately'
        }
        description={
          isSelf
            ? 'You are changing your own role. Your role and permissions are carried in your sign-in token, so saving ends all of your sessions and returns you to the login screen at once. Anything unsaved in another tab is lost.'
            : 'The role and its permissions are carried in the sign-in token, so saving ends all of this admin’s sessions to apply the change at once. They are returned to the login screen wherever they are signed in, and anything they have unsaved is lost.'
        }
      />
      <Form
        form={form}
        onFinish={onFinish}
        layout="vertical"
        size="large"
        initialValues={{ roleId: currentRoleId ?? undefined }}
      >
        <Form.Item name="roleId" label="Role" rules={[{ required: true }]}>
          <Select
            showSearch
            optionFilterProp="label"
            placeholder="Select a role"
            options={assignable.map(role => ({
              value: role.id,
              label: role.label || role.name
            }))}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default memo(AssignRoleModal);
